import {
  FC,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  useContext,
} from 'react';
import { useLocation } from 'react-router';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { WorkflowListTable } from '@/pages/workflow/component/workflow-list-table';
import { WorkflowSortField } from '@/types/workflow-types';
import { Button } from '@/components/ui/button';
import { PageTitle } from '@/components/page-title';
import { PageDescription } from '@/components/page-description';
import { useNotification } from '@/components/notifications';
import {
  CirclePlus,
  Clock,
  Grid,
  List,
  FileText,
  // Filter,
  ChevronDown,
  CalendarSearch,
} from 'lucide-react';
import HelpTooltip from '@/components/ui/help-tooltip';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CreateNewWorkflowModal } from './component/create-workflow-modal';
import { useWorkflowStore } from '@/stores/workflow-store';
import { useOrgStore } from '@/stores/org-store';
import { ROUTES } from '@/constants/routes';
import { OrgImage } from '@/components/org-image';
import { Checkbox } from '@/components/ui/checkbox';
import { SearchInput } from '@/components/ui/search-input';
import { Filter } from '@/components/filter';
import { useGetOrgLabels } from '@/hooks/queries/use-get-org-labels';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { AuthContext } from '@/context/auth-context';

export const WorkflowDashboardPage: FC = () => {
  const navigate = useOrgNavigate();
  const location = useLocation();
  const notification = useNotification();
  const {
    getWorkflowList,
    templateWorkflows,
    isLoadingWorkflows,
    resetLoadingState,
  } = useWorkflowStore();
  const { currentOrg } = useOrgStore();
  const { user } = useContext(AuthContext);
  const { isLabelsEnabled } = useLaunchDarkly();
  const { data: orgLabels = [] } = useGetOrgLabels();
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [showMyWorkflows, setShowMyWorkflows] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('owner') === 'me';
  });
  const [showSharedWithMe, setShowSharedWithMe] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('shared') === 'me';
  });
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    // Initialize from localStorage, default to 'list' if not found
    const savedViewMode = localStorage.getItem(
      'workflow-dashboard-view-mode',
    ) as 'grid' | 'list';
    return savedViewMode || 'list';
  });

  // Custom setter that persists to localStorage
  const handleViewModeChange = (mode: 'grid' | 'list') => {
    setViewMode(mode);
    localStorage.setItem('workflow-dashboard-view-mode', mode);
  };
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debounceTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  type SortDirection = 'asc' | 'desc';

  const [sortField, setSortField] = useState<WorkflowSortField>('updated_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const sortField = searchParams.get('sortField') as WorkflowSortField;
    const sortDirection = searchParams.get('sortDirection') as SortDirection;
    const searchQuery = searchParams.get('searchQuery') || '';
    const currentPage = parseInt(searchParams.get('currentPage') || '1', 10);
    const labelsParam = searchParams.get('labels');

    setSearchQuery(searchQuery);
    if (labelsParam) {
      setSelectedLabels(labelsParam.split(',').filter(Boolean));
    } else {
      setSelectedLabels([]);
    }

    setShowMyWorkflows(searchParams.get('owner') === 'me');
    setShowSharedWithMe(searchParams.get('shared') === 'me');

    // Clear any pending debounced updates when URL changes externally
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (sortField) {
      setSortField(sortField);
    }
    if (sortDirection) {
      setSortDirection(sortDirection);
    }
    if (currentPage) {
      setCurrentPage(currentPage);
    }
  }, [location.search]);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchWorkflows = async () => {
      if (!currentOrg?.orgid) return;

      try {
        await getWorkflowList(currentOrg.orgid, abortController.signal);
      } catch (err) {
        // Only show error if the request wasn't aborted
        if (!abortController.signal.aborted) {
          notification.error('Failed to fetch workflows');
          console.error('Error fetching workflows:', err);
        }
      }
    };

    fetchWorkflows();

    // Cleanup function to abort the request if component unmounts
    return () => {
      abortController.abort();
    };
  }, [notification, getWorkflowList, currentOrg?.orgid]);

  // Safety mechanism: Reset loading state if it gets stuck for too long
  useEffect(() => {
    if (isLoadingWorkflows) {
      const timeoutId = setTimeout(() => {
        console.warn('Loading state stuck for 60 seconds, resetting...');
        resetLoadingState();
      }, 60000); // 60 seconds

      return () => clearTimeout(timeoutId);
    }
  }, [isLoadingWorkflows, resetLoadingState]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleTemplateSelect = (templateId: string) => {
    // Handle template selection - navigate to workflow editor with template
    // Use query parameter instead of state so it persists across page refresh
    navigate(
      `${ROUTES.WORKFLOW.ROOT}/new?template=${encodeURIComponent(templateId)}`,
    );
  };

  const handleEditTemplate = (templateId: string) => {
    navigate(`${ROUTES.WORKFLOW.ROOT}/${templateId}`, {
      state: { isEditingTemplate: true },
    });
  };

  // Sorting functions
  const handleSort = (field: WorkflowSortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      const params = new URLSearchParams(location.search);
      params.set('sortDirection', sortDirection === 'asc' ? 'desc' : 'asc');
      params.set('currentPage', '1');
      navigate({
        pathname: location.pathname,
        search: params.toString(),
      });
    } else {
      // Set new field with default direction
      const params = new URLSearchParams(location.search);
      params.set('sortDirection', 'desc');
      params.set('sortField', field);
      params.set('currentPage', '1');
      navigate({
        pathname: location.pathname,
        search: params.toString(),
      });
    }
  };

  const handleSetCurrentPage = (page: number) => {
    const params = new URLSearchParams(location.search);
    params.set('currentPage', page.toString());
    navigate({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  const handleSearchQueryChange = useCallback(
    (query: string) => {
      // Update local state immediately for responsive input
      setSearchQuery(query);

      // Clear any existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Debounce URL update to reduce navigation overhead
      debounceTimerRef.current = setTimeout(() => {
        const params = new URLSearchParams(location.search);
        params.set('searchQuery', query);
        params.set('currentPage', '1');
        navigate(
          {
            pathname: location.pathname,
            search: params.toString(),
          },
          { replace: true },
        );
      }, 300);
    },
    [location.pathname, location.search, navigate],
  );

  const handleLabelFilterChange = useCallback(
    (labels: string[]) => {
      setSelectedLabels(labels);
      const params = new URLSearchParams(location.search);
      if (labels.length > 0) {
        params.set('labels', labels.join(','));
      } else {
        params.delete('labels');
      }
      params.set('currentPage', '1');
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true },
      );
    },
    [location.pathname, location.search, navigate],
  );

  const buildBaseFilterParams = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
    }
    const params = new URLSearchParams(location.search);
    if (searchQuery) {
      params.set('searchQuery', searchQuery);
    } else {
      params.delete('searchQuery');
    }
    params.set('currentPage', '1');
    return params;
  }, [searchQuery, location.search]);

  const handleToggleMyWorkflows = useCallback(() => {
    const next = !showMyWorkflows;
    setShowMyWorkflows(next);
    const params = buildBaseFilterParams();
    if (next) {
      params.set('owner', 'me');
    } else {
      params.delete('owner');
    }
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true },
    );
  }, [showMyWorkflows, buildBaseFilterParams, location.pathname, navigate]);

  const handleToggleSharedWithMe = useCallback(() => {
    const next = !showSharedWithMe;
    setShowSharedWithMe(next);
    const params = buildBaseFilterParams();
    if (next) {
      params.set('shared', 'me');
    } else {
      params.delete('shared');
    }
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true },
    );
  }, [showSharedWithMe, buildBaseFilterParams, location.pathname, navigate]);

  const labelFilterOptions = useMemo(
    () => orgLabels.map((label) => ({ label: label.name, value: label.name })),
    [orgLabels],
  );

  const getSortButtonText = () => {
    switch (sortField) {
      case 'name':
        return 'Name';
      case 'created_at':
        return 'Created';
      case 'updated_at':
        return 'Last modified';
      case 'last_run_at':
        return 'Last run';
      case 'total_runs':
        return 'Total runs';
      case 'latest_run_cost_usd':
        return 'Latest run cost';
      default:
        return 'Last modified';
    }
  };

  const getSortOptions = (): {
    field: WorkflowSortField;
    label: string;
    icon: React.ReactNode;
  }[] => [
    {
      field: 'updated_at',
      label: 'Last modified',
      icon: <Clock className='size-4' />,
    },
    {
      field: 'last_run_at',
      label: 'Last run',
      icon: <Clock className='size-4' />,
    },
    {
      field: 'total_runs',
      label: 'Total runs',
      icon: <FileText className='size-4' />,
    },
    {
      field: 'latest_run_cost_usd',
      label: 'Latest run cost',
      icon: <FileText className='size-4' />,
    },
    {
      field: 'name',
      label: 'Name',
      icon: <FileText className='size-4' />,
    },
    {
      field: 'created_at',
      label: 'Created',
      icon: <CalendarSearch className='size-4' />,
    },
  ];

  const userTemplates = templateWorkflows.filter(
    (t) =>
      user?.email &&
      t.created_by_email?.toLowerCase() === user.email.toLowerCase(),
  );

  // Combine existing JSON templates with backend templates
  const allTemplates = [
    {
      workflow_id: 'blank',
      publish_to_template: true,
      name: 'Blank Canvas',
      created_at: '2025-09-23T00:00:00.000Z',
      created_by_name: 'System',
      created_by_email: 'system@capitol.ai',
      icon: 'StickyNote',
      color: '#6366F1',
      bg_color: '#E0E7FF',
      updated_at: '2025-09-23T00:00:00.000Z',
      description: 'Create a Workflow from scratch',
    },
    {
      workflow_id: 'import-from-file',
      publish_to_template: true,
      name: 'Import from File',
      created_at: '2025-09-23T00:00:00.000Z',
      created_by_name: 'System',
      created_by_email: 'system@capitol.ai',
      icon: 'FileUp',
      color: '#0EA5E9',
      bg_color: '#E0F2FE',
      updated_at: '2025-09-23T00:00:00.000Z',
      description: 'Create a workflow from an exported workflow file',
    },
    ...userTemplates.map((workflow) => ({
      ...workflow,
      icon: workflow.icon || 'WorkflowIcon',
      color: workflow.color || '#FFFFFF',
      backgroundColor: workflow.bg_color || '#3B82F6',
    })),
  ];

  return (
    <>
      <div className='flex-1 min-h-0 overflow-auto'>
        <div className='flex p-8 2xl:max-w-[1280px] w-full mx-auto min-h-0'>
          <div className='w-full '>
            {/* Header section with title, search, filters, and new workflow button */}
            <div className='flex justify-between items-center mb-6 w-full'>
              <div className='flex flex-col items-start w-full'>
                <PageTitle
                  title={
                    currentOrg?.branding?.workflow_page_title ||
                    'Workflow composer'
                  }
                />
                <PageDescription>
                  Build and manage workflows that help your team get things
                  done, whether it's generating a slide deck, automating a task,
                  or creating something new from scratch. Search existing
                  workflows to jump back in, or start fresh with a template and
                  customize it to fit your needs.
                </PageDescription>
              </div>
              <div className='flex items-center justify-end shrink-0 ml-4'>
                <div
                  className={`flex items-center justify-end ${
                    currentOrg?.branding?.show_logo_text === false
                      ? 'h-12 max-w-none'
                      : 'h-12 max-w-48'
                  }`}
                >
                  <OrgImage
                    imageUrl={currentOrg?.branding?.logo_url}
                    className={`object-contain rounded-md h-12 w-auto ${
                      currentOrg?.branding?.show_logo_text === false
                        ? 'max-w-none'
                        : 'max-w-full'
                    }`}
                  />
                </div>
              </div>
            </div>
            <div className='flex flex-col xl:flex-row xl:items-center gap-3'>
              {/* Search bar — own row on small, inline on xl */}
              <HelpTooltip
                label='Search help'
                trigger={
                  <div className='w-full xl:w-64'>
                    <SearchInput
                      value={searchQuery}
                      onChange={handleSearchQueryChange}
                      placeholder='Search workflows'
                      aria-label='Search workflows'
                    />
                  </div>
                }
              >
                Search by name or description to quickly find a workflow.
              </HelpTooltip>

              {/* Controls — sort, labels, view toggles, new workflow */}
              <div className='flex items-center gap-3 xl:flex-1'>
                {/* Sort dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant='outline'
                      className='flex items-center gap-2'
                    >
                      <Clock className='size-4' />
                      {getSortButtonText()}
                      <span className='text-xs text-muted-foreground'>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                      <ChevronDown className='size-4' />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='start'>
                    {getSortOptions().map((option) => (
                      <DropdownMenuItem
                        key={option.field}
                        onClick={() => handleSort(option.field)}
                        className='flex items-center gap-2'
                      >
                        {option.icon}
                        {option.label}
                        {sortField === option.field && (
                          <span className='ml-auto text-xs text-muted-foreground'>
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Label filter */}
                {isLabelsEnabled && orgLabels.length > 0 && (
                  <Filter
                    title='Labels'
                    options={labelFilterOptions}
                    selected={selectedLabels}
                    setSelected={handleLabelFilterChange}
                    tooltip='Filter workflows by label'
                  />
                )}

                <div className='ml-auto flex items-center gap-2'>
                  {/* View toggle buttons */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={viewMode === 'grid' ? 'outline' : 'ghost'}
                        size='sm'
                        className='h-8 w-8 p-0'
                        onClick={() => handleViewModeChange('grid')}
                        aria-label='Grid View'
                      >
                        <Grid className='size-4' />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side='bottom'>Grid View</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={viewMode === 'list' ? 'outline' : 'ghost'}
                        size='sm'
                        className='h-8 w-8 p-0'
                        onClick={() => handleViewModeChange('list')}
                        aria-label='Table View'
                      >
                        <List className='size-4' />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side='bottom'>Table View</TooltipContent>
                  </Tooltip>
                  {/* New Workflow button */}
                  <Button onClick={() => setIsCreateModalOpen(true)}>
                    <CirclePlus className='size-4' />
                    New Workflow
                  </Button>
                </div>
              </div>
            </div>
            <div className='flex items-center gap-4 mt-3'>
              <div className='flex items-center gap-2'>
                <Checkbox
                  id='my-workflows'
                  checked={showMyWorkflows}
                  onCheckedChange={handleToggleMyWorkflows}
                />
                <label
                  htmlFor='my-workflows'
                  className='text-sm cursor-pointer select-none text-muted-foreground'
                >
                  My workflows
                </label>
              </div>
              <div className='flex items-center gap-2'>
                <Checkbox
                  id='shared-with-me'
                  checked={showSharedWithMe}
                  onCheckedChange={handleToggleSharedWithMe}
                />
                <label
                  htmlFor='shared-with-me'
                  className='text-sm cursor-pointer select-none text-muted-foreground'
                >
                  Shared
                </label>
                <HelpTooltip label='Shared info'>
                  Workflows shared with you directly or available to all org
                  members via default access.
                </HelpTooltip>
              </div>
            </div>
          </div>
        </div>

        <div className='flex px-8 pb-8 2xl:max-w-[1280px] w-full mx-auto min-h-[calc(100vh-200px)]'>
          <WorkflowListTable
            isLoading={isLoadingWorkflows}
            viewMode={viewMode}
            openCreateModal={() => setIsCreateModalOpen(true)}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            searchQuery={searchQuery}
            currentPage={currentPage}
            handleSetCurrentPage={handleSetCurrentPage}
            selectedLabels={selectedLabels}
            showMyWorkflows={showMyWorkflows}
            showSharedWithMe={showSharedWithMe}
          />
        </div>
      </div>
      {/* Create New Workflow Modal */}
      <CreateNewWorkflowModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        templates={allTemplates}
        onTemplateSelect={handleTemplateSelect}
        onEditTemplate={handleEditTemplate}
        isLoading={isLoadingWorkflows}
      />
    </>
  );
};
